/**
 * Server data (TanStack Query) — every read the screens make and every
 * mutation, with the query keys they invalidate. Nothing here is stored in
 * Zustand; the session query hands its view to the session store because
 * the HTTP client needs the CSRF token outside React.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type {
  ApiKeyView,
  CheckName,
  EntityProfile,
  EntityTax,
  EntitySummary,
  Envelope,
  HistoryItem,
  HomeView,
  Member,
  Purpose,
  ReviewItem,
  Role,
  SessionView,
  Signal,
  UsageView,
  Verification,
} from '../lib/types.js';
import { useSessionStore } from '../store/session.js';

export const keys = {
  session: ['session'] as const,
  home: ['home'] as const,
  records: ['records'] as const,
  record: (id: string) => ['record', id] as const,
  search: (q: string, live: boolean) => ['search', q, live ? 'live' : 'index'] as const,
  entity: (id: string) => ['entity', id] as const,
  history: (id: string) => ['entity', id, 'history'] as const,
  signals: (id: string) => ['entity', id, 'signals'] as const,
  review: ['review'] as const,
  team: ['team'] as const,
  keys: ['keys'] as const,
  usage: ['usage'] as const,
};

// ------------------------------------------------------------------ session

export function useSession() {
  const signIn = useSessionStore((s) => s.signIn);
  const signOut = useSessionStore((s) => s.signOut);
  return useQuery({
    queryKey: keys.session,
    queryFn: async () => {
      try {
        const view = await api<SessionView>('/app/api/session');
        signIn(view);
        return view;
      } catch (err) {
        signOut();
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (email: string) =>
      api<{ ok: true }>('/app/api/login', { method: 'POST', body: { email } }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const signOut = useSessionStore((s) => s.signOut);
  return useMutation({
    mutationFn: () => api<void>('/app/api/logout', { method: 'POST' }),
    onSettled: () => {
      signOut();
      qc.clear();
    },
  });
}

// ------------------------------------------------------------------ reads

export function useHome() {
  return useQuery({ queryKey: keys.home, queryFn: () => api<HomeView>('/app/api/home') });
}

export function useRecords() {
  return useQuery({
    queryKey: keys.records,
    queryFn: () => api<{ records: Verification[] }>('/app/api/records?limit=100'),
  });
}

export function useRecord(id: string) {
  return useQuery({
    queryKey: keys.record(id),
    queryFn: () =>
      api<Envelope<Verification>>(`/v1/verifications/${encodeURIComponent(id)}`),
  });
}

/**
 * `GET /v1/search`. The API reads PACRA and ZRA live when its index has
 * nothing for the query; `live` forces that read (`live=1`, the "Search the
 * registers live" action) and is part of the key so the index answer and
 * the live answer are two cache entries.
 */
export function useSearch(q: string, live = false) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: keys.search(trimmed, live),
    queryFn: () =>
      api<Envelope<EntitySummary[]>>(
        `/v1/search?q=${encodeURIComponent(trimmed)}&limit=25${live ? '&live=1' : ''}`,
      ),
    enabled: trimmed.length >= 2,
  });
}

export function useEntity(id: string) {
  return useQuery({
    queryKey: keys.entity(id),
    queryFn: () => api<Envelope<EntityProfile>>(`/v1/entities/${encodeURIComponent(id)}`),
  });
}

export function useEntityHistory(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.history(id),
    queryFn: () =>
      api<Envelope<HistoryItem[]>>(
        `/v1/entities/${encodeURIComponent(id)}/history?limit=25`,
      ),
    enabled,
  });
}

export function useEntitySignals(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.signals(id),
    queryFn: () =>
      api<Envelope<Signal[]>>(
        `/v1/entities/${encodeURIComponent(id)}/signals?status=all&limit=25`,
      ),
    enabled,
  });
}

export function useReviewQueue() {
  return useQuery({
    queryKey: keys.review,
    queryFn: () => api<{ items: ReviewItem[] }>('/app/api/review'),
  });
}

export function useTeam() {
  return useQuery({
    queryKey: keys.team,
    queryFn: () => api<{ members: Member[] }>('/app/api/team'),
  });
}

export function useKeys() {
  return useQuery({
    queryKey: keys.keys,
    queryFn: () => api<{ keys: ApiKeyView[] }>('/app/api/keys'),
  });
}

export function useUsage() {
  return useQuery({
    queryKey: keys.usage,
    queryFn: () => api<UsageView>('/app/api/usage'),
  });
}

// ------------------------------------------------------------------ mutations

export interface IssueInput {
  entity_id: string;
  checks: CheckName[];
  purpose: Purpose;
  reference?: string;
}

export function useIssueVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IssueInput) =>
      api<Envelope<Verification>>('/v1/verifications', {
        method: 'POST',
        body: {
          entity_id: input.entity_id,
          checks: input.checks,
          purpose: input.purpose,
          ...(input.reference ? { reference: input.reference } : {}),
        },
      }),
    onSuccess: (env) => {
      qc.setQueryData(keys.record(env.data.id), env);
      void qc.invalidateQueries({ queryKey: keys.home });
      void qc.invalidateQueries({ queryKey: keys.records });
      void qc.invalidateQueries({ queryKey: keys.session });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
}

export interface ReVerifyInput {
  entity_id: string;
  /** The original record's purpose; a re-verify defaults to `periodic_review`. */
  purpose?: Purpose | null;
  /** The original record's reference, preserved on the fresh record. */
  reference?: string | null;
}

/**
 * Issue a FRESH record for a subject that already has one ("re-verify").
 * Deliberately never idempotent: the console's HTTP client (`lib/api.ts`)
 * sends no `Idempotency-Key` header anywhere, so the server can never
 * replay an earlier record — every re-verify reads the registers again.
 * Checks are left to the server default (registry, tax, vat, returns).
 */
export function useReVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReVerifyInput) =>
      api<Envelope<Verification>>('/v1/verifications', {
        method: 'POST',
        body: {
          entity_id: input.entity_id,
          purpose: input.purpose ?? 'periodic_review',
          ...(input.reference ? { reference: input.reference } : {}),
        },
      }),
    onSuccess: (env, input) => {
      qc.setQueryData(keys.record(env.data.id), env);
      void qc.invalidateQueries({ queryKey: keys.home });
      void qc.invalidateQueries({ queryKey: keys.records });
      void qc.invalidateQueries({ queryKey: keys.session });
      void qc.invalidateQueries({ queryKey: keys.usage });
      void qc.invalidateQueries({ queryKey: keys.entity(input.entity_id) });
    },
  });
}

export function useNoMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; purpose?: Purpose; reference?: string }) =>
      api<Envelope<Verification>>('/v1/no-match', { method: 'POST', body: input }),
    onSuccess: (env) => {
      qc.setQueryData(keys.record(env.data.id), env);
      void qc.invalidateQueries({ queryKey: keys.home });
      void qc.invalidateQueries({ queryKey: keys.records });
      void qc.invalidateQueries({ queryKey: keys.session });
    },
  });
}

/**
 * Link a business name to its owner's personal TPIN (owner fact 2026-09-22):
 * the server reads ZRA live by TPIN, keeps the record as personal data and
 * joins it as a human-asserted link. Admin|verifier; auditors get 403.
 */
export function useLinkOwnerTpin(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tpin: string; replace?: boolean }) =>
      api<Envelope<EntityTax>>(
        `/v1/entities/${encodeURIComponent(entityId)}/owner-tpin`,
        {
          method: 'POST',
          body: { tpin: input.tpin, ...(input.replace ? { replace: true } : {}) },
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entity(entityId) });
      void qc.invalidateQueries({ queryKey: keys.history(entityId) });
    },
  });
}

/** Unlink the owner's TPIN (the link is kept as rejected server-side). */
export function useUnlinkOwnerTpin(entityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<Envelope<EntityTax>>(
        `/v1/entities/${encodeURIComponent(entityId)}/owner-tpin`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entity(entityId) });
      void qc.invalidateQueries({ queryKey: keys.history(entityId) });
    },
  });
}

export function useDecideReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      entityId: string;
      source: string;
      sourceRecordId: string | null;
    }) =>
      api<{ ok: true; confirmed: string | null; rejected: string[] }>(
        `/app/api/review/${encodeURIComponent(input.entityId)}`,
        {
          method: 'POST',
          body: { source: input.source, source_record_id: input.sourceRecordId },
        },
      ),
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: keys.review });
      void qc.invalidateQueries({ queryKey: keys.home });
      void qc.invalidateQueries({ queryKey: keys.entity(input.entityId) });
    },
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: Role }) =>
      api<{ member: Member }>('/app/api/team', { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.team }),
  });
}

export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; role: Role }) =>
      api<{ member: Member }>(`/app/api/team/${input.id}`, {
        method: 'PATCH',
        body: { role: input.role },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.team }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/app/api/team/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.team }),
  });
}

export function useCreateKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; env: 'live' | 'test' }) =>
      api<{ key: string; row: ApiKeyView }>('/app/api/keys', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.keys });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
}

export function useRevokeKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/app/api/keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.keys });
      void qc.invalidateQueries({ queryKey: keys.usage });
    },
  });
}
